// hooks/useAuth.js
// BuzzInApp — Autenticación y estado del usuario
//
// Maneja:
//   - Login con Google
//   - Verificación SMS (segundo factor)
//   - Creación/lectura del perfil en Firestore
//   - Trust score inicial
//   - Flag de cuenta nueva

import { useState, useEffect, useCallback, useRef } from "react";
import { RecaptchaVerifier } from "firebase/auth";
import {
  auth,
  sendSmsCode,
  signInWithGoogle,
  logOut,
  onAuthChange,
  getDocument,
  setDocument,
  serverTimestamp,
  COLLECTIONS,
} from "../config/firebase";
import { TRUST_SCORE } from "../config/security";
import { isNewAccount } from "../security/fraudDetector";

// ─── Estado inicial ───────────────────────────────────────────────────────────
const INITIAL_STATE = {
  user:             null,   // Firebase Auth user
  profile:          null,   // Documento de Firestore (trustScore, phone, etc.)
  phoneVerified:    false,
  isNewAccount:     false,
  loading:          true,
  error:            null,
};

const useAuth = () => {
  const [state, setState]               = useState(INITIAL_STATE);
  const [smsLoading, setSmsLoading]     = useState(false);
  const [smsError, setSmsError]         = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaRef                    = useRef(null);

  // ─── Suscripción al estado de Firebase Auth ─────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthChange(async (firebaseUser) => {
      if (!firebaseUser) {
        setState({ ...INITIAL_STATE, loading: false });
        return;
      }

      // Leer o crear perfil en Firestore
      const profile = await ensureProfile(firebaseUser);

      setState({
        user:          firebaseUser,
        profile,
        phoneVerified: profile?.phoneVerified ?? false,
        isNewAccount:  isNewAccount(profile?.createdAt?.toDate?.() ?? new Date()),
        loading:       false,
        error:         null,
      });
    });

    return unsubscribe;
  }, []);

  // ─── Crear perfil si no existe ───────────────────────────────────────────
  const ensureProfile = async (firebaseUser) => {
    const existing = await getDocument(COLLECTIONS.USERS, firebaseUser.uid);
    if (existing) return existing;

    const newProfile = {
      uid:           firebaseUser.uid,
      email:         firebaseUser.email,
      displayName:   firebaseUser.displayName,
      photoURL:      firebaseUser.photoURL,
      phone:         null,
      phoneVerified: false,
      trustScore:    TRUST_SCORE.INITIAL_SCORE,
      totalClicks:   0,
      totalRedemptions: 0,
      createdAt:     serverTimestamp(),
      updatedAt:     serverTimestamp(),
    };

    await setDocument(COLLECTIONS.USERS, firebaseUser.uid, newProfile);
    return newProfile;
  };

  // ─── Login Google ────────────────────────────────────────────────────────
  const loginWithGoogle = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await signInWithGoogle();
      // onAuthChange se dispara y actualiza el estado
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err.code === "auth/popup-closed-by-user" ? null : "google_failed",
      }));
    }
  }, []);

  // ─── Inicializar reCAPTCHA ────────────────────────────────────────────────
  // Siempre crea un verifier nuevo — nunca reutiliza uno ya consumido por Firebase.
  // DEV: size "normal" (widget visible). Cambiar a "invisible" en producción.
  const initRecaptcha = useCallback((containerId = "recaptcha-container") => {
    // Limpiar verifier anterior si existe
    if (recaptchaRef.current) {
      try { recaptchaRef.current.clear(); } catch (_) {}
      recaptchaRef.current = null;
    }

    // Limpiar el contenedor del DOM para que el widget se pueda re-renderizar
    const container = document.getElementById(containerId);
    if (container) container.innerHTML = "";

    const verifier = new RecaptchaVerifier(auth, containerId, {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {
        recaptchaRef.current = null;
      },
    });

    verifier.render();
    recaptchaRef.current = verifier;
    return verifier;
  }, []);

  // ─── Enviar código SMS ───────────────────────────────────────────────────
  const sendSms = useCallback(
    async (phoneNumber, containerId = "recaptcha-container") => {
      setSmsLoading(true);
      setSmsError(null);

      try {
        // Siempre inicializa un verifier fresco para cada intento
        const verifier = initRecaptcha(containerId);
        const result   = await sendSmsCode(phoneNumber, verifier);
        setConfirmationResult(result);
        setSmsLoading(false);
        return { success: true };
      } catch (err) {
        // En caso de error limpiar el verifier para que el usuario pueda reintentar
        if (recaptchaRef.current) {
          try { recaptchaRef.current.clear(); } catch (_) {}
          recaptchaRef.current = null;
        }
        setSmsError("phone_failed");
        setSmsLoading(false);
        return { success: false, error: err.code };
      }
    },
    [initRecaptcha]
  );

  // ─── Verificar código SMS ────────────────────────────────────────────────
  const verifySmsCode = useCallback(
    async (code) => {
      if (!confirmationResult) {
        setSmsError("no_confirmation_result");
        return { success: false };
      }

      setSmsLoading(true);
      setSmsError(null);

      try {
        await confirmationResult.confirm(code);

        // Marcar teléfono verificado en Firestore
        if (state.user) {
          await setDocument(COLLECTIONS.USERS, state.user.uid, {
            phoneVerified: true,
            phone:         state.user.phoneNumber,
            updatedAt:     serverTimestamp(),
          });

          setState((s) => ({
            ...s,
            phoneVerified: true,
            profile: { ...s.profile, phoneVerified: true },
          }));
        }

        setSmsLoading(false);
        return { success: true };
      } catch {
        setSmsError("code_failed");
        setSmsLoading(false);
        return { success: false, error: "code_failed" };
      }
    },
    [confirmationResult, state.user]
  );

  // ─── Logout ──────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    await logOut();
    setState({ ...INITIAL_STATE, loading: false });
  }, []);

  // ─── Refresh profile (después de canjes, ratings, etc.) ─────────────────
  const refreshProfile = useCallback(async () => {
    if (!state.user) return;
    const profile = await getDocument(COLLECTIONS.USERS, state.user.uid);
    setState((s) => ({ ...s, profile }));
  }, [state.user]);

  return {
    // Estado
    user:          state.user,
    profile:       state.profile,
    phoneVerified: state.phoneVerified,
    isNewAccount:  state.isNewAccount,
    isLoggedIn:    !!state.user,
    loading:       state.loading,
    error:         state.error,

    // SMS
    smsLoading,
    smsError,
    hasPendingSms: !!confirmationResult,

    // Acciones
    loginWithGoogle,
    sendSms,
    verifySmsCode,
    initRecaptcha,
    logout,
    refreshProfile,
  };
};

export default useAuth;
